import i18n from 'i18next';
import type { BackendModule } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import ICU from 'i18next-icu';
import { initReactI18next } from 'react-i18next';
import { I18nResource } from '@/i18n/i18n-supported-language';
import { i18nNamespaces } from '@/i18n/i18nNamespaces';
import { staticData, type I18nStaticDataKey } from '@/i18n/staticData';

export const defaultNS = 'common';

// Loads one `locale:namespace` JSON chunk on demand, so each resource file stays its own
// dynamic-import chunk. This replaces the Tolgee SDK wrapper — see
// docs/adr/0009-i18n-translations-in-repo-tolgee-retired.md.
const lazyStaticDataBackend: BackendModule = {
  type: 'backend',
  init() {},
  read(language, namespace, callback) {
    const load = staticData[`${language}:${namespace}` as I18nStaticDataKey];
    if (!load) {
      callback(new Error(`No static translation data for ${language}:${namespace}`), null);
      return;
    }
    load().then(
      (module) => callback(null, module.default),
      (error) => callback(error instanceof Error ? error : new Error(String(error)), null)
    );
  },
};

i18n
  .use(lazyStaticDataBackend)
  .use(ICU)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    supportedLngs: Object.values(I18nResource),
    fallbackLng: I18nResource.TH_TH,
    debug: false,
    ns: i18nNamespaces,
    defaultNS,
    interpolation: {
      escapeValue: true,
    },
    // Committed chunks keep `null` for untranslated keys, where Tolgee used to drop them before
    // serving. Treating null as missing sends those keys down the Thai fallback chain again.
    returnNull: false,
    returnEmptyString: true,
    parseMissingKeyHandler: () => '',
    react: {
      bindI18n: 'languageChanged',
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p'],
      useSuspense: false,
    },
    // `caches` defaults to localStorage already; naming it keeps the persistence contract
    // explicit — i18next writes `i18nextLng` at init detection and on every in-app switch.
    detection: {
      order: ['querystring', 'localStorage'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },
  });

export default i18n;
